import { useEffect, useRef, useState } from "react";
import { ImageUp, Loader2, Save, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompanyLogo } from "@/hooks/useCompanyLogo";

const FIELDS = [["name", "Company name"], ["address", "Address"], ["phone", "Phone"], ["email", "Email"], ["website", "Website"]];
const LOGO_EXT = ["png", "jpg", "jpeg", "svg", "webp"];
export const LOGO_ERR = "Logo must be PNG, JPG, SVG or WebP under 5 MB";

export function CompanySection({ company }) {
  const { setCompany, user, refresh } = useAuth();
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm({ ...Object.fromEntries(FIELDS.map(([k]) => [k, company[k] || ""])), rep_name: user?.name || "" }),
    [company, user?.name]);

  const save = async () => {
    if (!form.name?.trim()) return setError("Company name is required");
    setError("");
    setSaving(true);
    try {
      const { data } = await api.put("/settings/company", form);
      setCompany(data);
      refresh();
      toast.success("Company details saved");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-2xl border border-slate-800 bg-[#F2F2F7]/60 p-6" data-testid="settings-company-section">
      <h2 className="text-base font-bold text-slate-100 md:text-lg">Company details</h2>
      <p className="mt-1 text-sm text-slate-400">Shown on your PDF estimates.</p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        {FIELDS.map(([k, label]) => (
          <div key={k} className={`space-y-1.5 ${k === "address" ? "sm:col-span-2" : ""}`}>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">{label}</Label>
            <Input data-testid={`company-${k}-input`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              className="h-10 border-slate-800 bg-slate-900/70 text-slate-100" />
          </div>
        ))}
        <div className="space-y-1.5 sm:col-span-2">
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Your name (sales representative on estimates)</Label>
          <Input data-testid="company-rep-name-input" value={form.rep_name || ""} onChange={(e) => setForm({ ...form, rep_name: e.target.value })}
            className="h-10 border-slate-800 bg-slate-900/70 text-slate-100" />
        </div>
      </div>
      {error && <p data-testid="company-details-error" className="mt-3 text-xs text-red-400">{error}</p>}
      <button data-testid="save-company-button" onClick={save} disabled={saving}
        className="mt-6 inline-flex h-10 items-center gap-2 rounded-full bg-amber-500 px-5 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-400 disabled:opacity-60">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save details
      </button>
    </section>
  );
}

export function LogoSection() {
  const { company, setCompany } = useAuth();
  const logo = useCompanyLogo();
  const input = useRef(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!file.name.includes(".") || !LOGO_EXT.includes(ext) || file.size > 5 * 1024 * 1024) return setError(LOGO_ERR);
    setError("");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/settings/logo", fd);
      setCompany(data);
      toast.success("Logo uploaded");
    } catch (e) {
      setError(errorMessage(e, LOGO_ERR));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    try {
      const { data } = await api.delete("/settings/logo");
      setCompany(data);
      toast.success("Logo removed");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <section className="rounded-2xl border border-slate-800 bg-[#F2F2F7]/60 p-6" data-testid="settings-logo-section">
      <h2 className="text-base font-bold text-slate-100 md:text-lg">Logo</h2>
      <p className="mt-1 text-sm text-slate-400">PNG, JPG, SVG or WebP up to 5 MB. Without a logo, your company name is shown.</p>
      <div data-testid="logo-preview" className="mt-6 grid h-36 place-items-center rounded-xl border border-slate-800 bg-white/95 p-4">
        {logo ? (
          <img data-testid="logo-preview-image" src={logo} alt="Company logo" className="max-h-full max-w-full object-contain" />
        ) : (
          <span data-testid="logo-fallback-name" className="font-display text-xl font-bold text-slate-50">{company?.name}</span>
        )}
      </div>
      <input ref={input} type="file" accept=".png,.jpg,.jpeg,.svg,.webp" className="hidden" data-testid="settings-logo-upload-input"
        onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
      {error && <p data-testid="logo-error" className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">{error}</p>}
      <div className="mt-5 flex gap-3">
        <button data-testid="upload-logo-button" onClick={() => input.current?.click()} disabled={busy}
          className="inline-flex h-10 items-center gap-2 rounded-full border border-amber-500/40 px-5 text-sm font-medium text-amber-300 transition-colors hover:bg-amber-500/10 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageUp className="h-4 w-4" />} {company?.has_logo ? "Replace logo" : "Upload logo"}
        </button>
        {company?.has_logo && (
          <button data-testid="remove-logo-button" onClick={remove}
            className="inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm text-slate-400 transition-colors hover:bg-red-500/10 hover:text-red-300">
            <X className="h-4 w-4" /> Remove
          </button>
        )}
      </div>
    </section>
  );
}
